import { CircularProgress, Rating, Skeleton, Stack } from "@mui/material";
import { Button } from "antd";
import {
  collection,
  getDocs,
  limit,
  query,
  startAfter,
  where,
} from "firebase/firestore";
import React, { useEffect, useState, useMemo } from "react";
import { Helmet } from "react-helmet-async";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { db } from "../Firebase/firebase";
import "./Posts.css";

// Category-specific SEO configurations
const categorySeoConfig = {
  "GERD-Friendly": {
    title: "GERD-Friendly Recipes | Low-Acid Meals for Acid Reflux | GentleGourmet",
    description:
      "Browse our collection of GERD-friendly recipes designed to minimize acid reflux triggers. Delicious, low-acid meals that are gentle on your stomach.",
    keywords:
      "GERD recipes, acid reflux friendly, low acid meals, heartburn diet, GERD diet, acid reflux recipes",
  },
  "IBS-Friendly": {
    title: "IBS-Friendly Recipes | Low FODMAP Meals | GentleGourmet",
    description:
      "Discover IBS-friendly recipes including low FODMAP options. Carefully crafted meals to help manage IBS symptoms without sacrificing flavor.",
    keywords:
      "IBS recipes, low FODMAP meals, IBS diet, irritable bowel syndrome food, gut friendly recipes",
  },
  "Gastritis-Friendly": {
    title: "Gastritis-Friendly Recipes | Soothing Meals for Sensitive Stomachs | GentleGourmet",
    description:
      "Find gentle, gastritis-friendly recipes that soothe your stomach. Anti-inflammatory meals designed for sensitive digestive systems.",
    keywords:
      "gastritis recipes, stomach friendly meals, anti-inflammatory diet, gentle foods, gastritis diet",
  },
  "Barretts-Esophagus-Friendly": {
    title: "Barrett's Esophagus-Friendly Recipes | Safe Meals | GentleGourmet",
    description:
      "Explore Barrett's esophagus-friendly recipes. Carefully selected meals that support esophageal health and minimize discomfort.",
    keywords:
      "Barrett's esophagus recipes, esophageal health, safe eating, digestive wellness",
  },
  International: {
    title: "International Digestive-Friendly Recipes | Global Cuisine | GentleGourmet",
    description:
      "Explore digestive-friendly recipes from around the world. International cuisines adapted for GERD, IBS, and gastritis sufferers.",
    keywords:
      "international recipes, global digestive friendly, world cuisine GERD, multicultural healthy recipes",
  },
};

export default function RecipeCategoryAndType() {
  const [numOfPostsToLoad, setNumOfPostsToLoad] = useState(6);
  const [lastVisiblePost, setLastVisiblePost] = useState(null);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [posts, setPosts] = useState([]);
  const [slicedEmail, setSlicedEmail] = useState([]);
  const { explore, recipe_category, recipe_type } = useParams();
  const navigate = useNavigate();
  const searchRef = collection(db, "posts");

  // SEO Data - Dynamic based on category and type
  const seoData = useMemo(() => {
    const categoryConfig = categorySeoConfig[recipe_category] || {
      title: `${recipe_category} Recipes | GentleGourmet`,
      description: `Browse ${recipe_category} digestive-friendly recipes. Find delicious meals that are gentle on your stomach.`,
      keywords: `${recipe_category} recipes, digestive friendly, healthy meals`,
    };

    // If a specific type is selected, modify the SEO
    if (recipe_type && recipe_type !== "all") {
      return {
        title: `${recipe_type} ${recipe_category} Recipes | GentleGourmet`,
        description: `Discover ${recipe_type} recipes in our ${recipe_category} collection. Delicious, digestive-friendly meals for your dietary needs.`,
        keywords: `${recipe_type}, ${recipe_category}, digestive recipes, healthy meals, ${categoryConfig.keywords}`,
        canonicalUrl: `https://gentlegourmet.app/explore/${encodeURIComponent(recipe_category)}/${encodeURIComponent(recipe_type)}`,
        ogImage: "https://gentlegourmet.app/images/og-recipes.jpg",
      };
    }

    return {
      ...categoryConfig,
      canonicalUrl: `https://gentlegourmet.app/explore/${encodeURIComponent(recipe_category)}/all`,
      ogImage: "https://gentlegourmet.app/images/og-recipes.jpg",
    };
  }, [recipe_category, recipe_type]);

  // JSON-LD Structured Data
  const jsonLdCollection = useMemo(
    () => ({
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      name: seoData.title,
      description: seoData.description,
      url: seoData.canonicalUrl,
      isPartOf: {
        "@type": "WebSite",
        name: "GentleGourmet",
        url: "https://gentlegourmet.app",
      },
      about: {
        "@type": "Thing",
        name: recipe_category,
        description: `${recipe_category} digestive-friendly recipes`,
      },
    }),
    [seoData, recipe_category]
  );

  const jsonLdBreadcrumbs = useMemo(
    () => ({
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        {
          "@type": "ListItem",
          position: 1,
          name: "Home",
          item: "https://gentlegourmet.app/",
        },
        {
          "@type": "ListItem",
          position: 2,
          name: recipe_category,
          item: `https://gentlegourmet.app/explore/${encodeURIComponent(recipe_category)}/all`,
        },
        ...(recipe_type && recipe_type !== "all"
          ? [
              {
                "@type": "ListItem",
                position: 3,
                name: recipe_type,
                item: seoData.canonicalUrl,
              },
            ]
          : []),
      ],
    }),
    [recipe_category, recipe_type, seoData]
  );

  // Reset and fetch posts when category or type changes
  useEffect(() => {
    async function fetchPosts() {
      setInitialLoading(true);
      setPosts([]);
      setSlicedEmail([]);
      setLastVisiblePost(null);

      let queryRef = buildQuery(recipe_category, recipe_type);
      const docSnap = await getDocs(queryRef);
      const lastVisible = docSnap.docs[docSnap.docs.length - 1];
      setLastVisiblePost(lastVisible);

      const newPosts = [];
      const newEmails = [];

      docSnap.forEach((doc) => {
        newPosts.push(doc);
        newEmails.push(doc.data().email);
      });

      setPosts(newPosts);
      setSlicedEmail(newEmails);
      setInitialLoading(false);
    }

    fetchPosts();
  }, [recipe_category, recipe_type]);

  function buildQuery(category, type, startAfterDoc = null) {
    let baseQuery = searchRef;

    if (category !== "International") {
      baseQuery = query(
        baseQuery,
        where("category", "==", category),
        where("privatePosts", "==", false)
      );
    } else {
      baseQuery = query(
        baseQuery,
        where("international", "==", true),
        where("privatePosts", "==", false)
      );
    }

    if (type !== "all") {
      baseQuery = query(baseQuery, where("tags", "array-contains", type));
    }

    if (startAfterDoc) {
      baseQuery = query(baseQuery, startAfter(startAfterDoc));
    }

    baseQuery = query(baseQuery, limit(numOfPostsToLoad));
    return baseQuery;
  }

  async function loadMorePosts() {
    setLoading(true);
    if (lastVisiblePost) {
      let queryRef = buildQuery(recipe_category, recipe_type, lastVisiblePost);
      const docSnap = await getDocs(queryRef);
      const lastVisible = docSnap.docs[docSnap.docs.length - 1];
      setLastVisiblePost(lastVisible);

      const newPosts = [...posts];
      const newEmails = [...slicedEmail];

      docSnap.forEach((doc) => {
        newPosts.push(doc);
        newEmails.push(doc.data().email);
      });

      setPosts(newPosts);
      setSlicedEmail(newEmails);
    }
    setLoading(false);
  }

  // Handle tag click - navigate and reset
  const handleTagClick = (category, tag) => {
    navigate(
      `/explore/${encodeURIComponent(category)}/${encodeURIComponent(tag)}`,
      { replace: false }
    );
    // The useEffect will handle resetting posts when the URL params change
  };

  // Handle category click
  const handleCategoryClick = (category) => {
    navigate(`/explore/${encodeURIComponent(category)}/all`, {
      replace: false,
    });
  };

  function manipulateDescription(postDesc) {
    let a = postDesc.data().desc;
    if (postDesc.data().desc.length >= 100) {
      a = postDesc.data().desc.substr(0, 100);
      a += "...";
    }
    return a;
  }

  // Loading skeleton for better UX
  const PostSkeleton = () => (
    <div className="w-full p-4">
      <div className="animate-pulse">
        <div className="bg-gray-300 h-48 rounded-t-lg mb-4"></div>
        <div className="bg-gray-300 h-4 rounded mb-2"></div>
        <div className="bg-gray-300 h-4 rounded w-3/4 mb-4"></div>
        <div className="flex space-x-2 mb-4">
          <div className="bg-gray-300 h-6 rounded-full w-20"></div>
          <div className="bg-gray-300 h-6 rounded-full w-16"></div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      {/* SEO Head Tags */}
      <Helmet>
        <html lang="en" />
        <title>{seoData.title}</title>
        <meta name="description" content={seoData.description} />
        <meta name="keywords" content={seoData.keywords} />
        <link rel="canonical" href={seoData.canonicalUrl} />
        <meta name="robots" content="index, follow, max-image-preview:large" />
        <meta property="og:title" content={seoData.title} />
        <meta property="og:description" content={seoData.description} />
        <meta property="og:url" content={seoData.canonicalUrl} />
        <meta property="og:image" content={seoData.ogImage} />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="GentleGourmet" />
        <meta property="og:locale" content="en_US" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={seoData.title} />
        <meta name="twitter:description" content={seoData.description} />
        <meta name="twitter:image" content={seoData.ogImage} />
        <script type="application/ld+json">
          {JSON.stringify(jsonLdCollection)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(jsonLdBreadcrumbs)}
        </script>
      </Helmet>

      {/* Header with current filter info */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            {recipe_type && recipe_type !== "all"
              ? `${recipe_type} Recipes`
              : `${recipe_category} Recipes`}
          </h1>
          {recipe_type && recipe_type !== "all" && (
            <p className="text-gray-600">
              in <span className="font-semibold">{recipe_category}</span>
            </p>
          )}
        </div>
      </div>

      {/* Posts Grid */}
      <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-6">
        {initialLoading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            {[...Array(6)].map((_, index) => (
              <PostSkeleton key={index} />
            ))}
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
              {posts.map((post) => (
                <div
                  key={post.id}
                  className="bg-white rounded-xl shadow-md overflow-hidden hover:shadow-lg transition-shadow duration-300 border border-gray-100"
                >
                  {/* Image */}
                  <Link
                    to={`/post/${post.data().category}/${post.id}`}
                    className="block aspect-w-16 aspect-h-9"
                  >
                    <img
                      src={
                        post.data().imageUrl ||
                        "https://firebasestorage.googleapis.com/v0/b/gingerrmarket.appspot.com/o/defaultImages%2Fphoto-1546069901-ba9599a7e63c.jpg?alt=media&token=c55c094f-91f0-4d7a-85d4-8ae2d125cce2"
                      }
                      alt={post.data().title}
                      className="w-full h-48 sm:h-56 object-cover"
                    />
                  </Link>

                  {/* Content */}
                  <div className="p-4 sm:p-6">
                    {/* Title */}
                    <Link
                      to={`/post/${post.data().category}/${post.id}`}
                      className="block mb-3"
                    >
                      <h2 className="text-lg sm:text-xl font-bold text-gray-900 line-clamp-2 hover:text-blue-600 transition-colors">
                        {post.data().title}
                      </h2>
                    </Link>

                    {/* Calories and Rating */}
                    <div className="flex items-center justify-between mb-4">
                      <span className="text-sm font-medium text-gray-500 bg-gray-100 px-2 py-1 rounded-full">
                        {post.data().calorieTotal} cal
                      </span>
                      <Rating
                        name="read-only"
                        value={post.data().rating || 0}
                        size="small"
                        readOnly
                      />
                    </div>

                    {/* Description */}
                    <p className="text-gray-600 text-sm mb-4 line-clamp-3">
                      {post.data().desc && manipulateDescription(post)}
                    </p>

                    {/* Tags */}
                    <div className="mb-4">
                      <div className="flex flex-wrap gap-2">
                        {Array.isArray(post.data().tags) &&
                          post
                            .data()
                            .tags.slice(0, 3)
                            .map((tag, index) => (
                              <button
                                key={index}
                                onClick={() =>
                                  handleTagClick(post.data().category, tag)
                                }
                                className="inline-block bg-blue-50 text-blue-700 rounded-full px-3 py-1 text-xs font-medium hover:bg-blue-100 transition-colors cursor-pointer border border-blue-200"
                              >
                                #{tag}
                              </button>
                            ))}
                        {post.data().tags && post.data().tags.length > 3 && (
                          <span className="text-xs text-gray-500 self-center">
                            +{post.data().tags.length - 3} more
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Category and Author */}
                    <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                      <button
                        onClick={() =>
                          handleCategoryClick(post.data().category)
                        }
                        className="text-sm font-medium text-green-600 hover:text-green-700 bg-green-50 px-3 py-1 rounded-full transition-colors cursor-pointer"
                      >
                        {post.data().category}
                      </button>
                      <Link
                        to={`/profile/${post.data().username_of_user}`}
                        className="text-sm text-gray-500 hover:text-gray-700 transition-colors"
                      >
                        By: {post.data().username_of_user || "Anonymous"}
                      </Link>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Load More Button */}
            {lastVisiblePost && (
              <div className="flex justify-center mt-8 sm:mt-12">
                <Button
                  onClick={loadMorePosts}
                  loading={loading}
                  size="large"
                  className="h-12 px-8 text-base font-medium bg-blue-600 text-white border-0 hover:bg-blue-700"
                >
                  Load More Recipes
                </Button>
              </div>
            )}

            {/* No posts message */}
            {posts.length === 0 && !initialLoading && (
              <div className="text-center py-12">
                <div className="text-gray-400 text-6xl mb-4">🍳</div>
                <h3 className="text-xl font-semibold text-gray-600 mb-2">
                  No recipes found
                </h3>
                <p className="text-gray-500">
                  Try adjusting your filters or check back later for new
                  recipes.
                </p>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
